













































































































import {
  defineComponent,
  ref,
  Ref,
  onMounted,
  watch,
} from '@vue/composition-api';
import { DataTableHeader, DataOptions } from 'vuetify';
import { ErrorsRowWithUser, ErrorStatus, SortType } from '@oare/types';
import sl from '@/serviceLocator';
import useQueryParam from '@/hooks/useQueryParam';
import ErrorLogDialog from '@/views/Admin/ErrorLog/components/ErrorLogDialog.vue';
import { DateTime } from 'luxon';
import { resetAdminBadge } from '@/utils';

export default defineComponent({
  components: {
    ErrorLogDialog,
  },
  setup() {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const _ = sl.get('lodash');

    const loading = ref(false);
    const searchLoading = ref(false);
    const showErrorDetails = ref(false);
    const listHeaders: Ref<DataTableHeader[]> = ref([
      { text: 'Status', value: 'status', width: '10%' },
      { text: 'Timestamp', value: 'timestamp', width: '20%' },
      { text: 'User', value: 'userName', width: '15%' },
      { text: 'Description', value: 'description', width: '40%' },
      {
        text: 'Stacktrace',
        value: 'stacktrace',
        width: '15%',
        sortable: false,
      },
    ]);

    const errorList: Ref<ErrorsRowWithUser[]> = ref([]);
    const selectedErrors: Ref<ErrorsRowWithUser[]> = ref([]);
    const dialogError: Ref<Partial<ErrorsRowWithUser>> = ref({});
    const serverCount = ref(0);

    const page = useQueryParam('page', '1', false);
    const limit = useQueryParam('rows', '25', true);
    const sort = useQueryParam('sort', 'timestamp', true);
    const desc = useQueryParam('desc', 'true', true);
    const status = useQueryParam('status', 'New', true);
    const user = useQueryParam('user', '', true);
    const description = useQueryParam('description', '', true);
    const stacktrace = useQueryParam('stacktrace', '', true);

    const statusOptions: ErrorStatus[] = ['New', 'In Progress', 'Resolved'];

    const searchOptions: Ref<DataOptions> = ref({
      page: Number(page.value),
      itemsPerPage: Number(limit.value),
      sortBy: [sort.value],
      sortDesc: [Boolean(desc.value)],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true,
    });

    onMounted(async () => {
      try {
        loading.value = true;
        await getErrorLog();
      } finally {
        loading.value = false;
      }
    });

    const getErrorLog = async () => {
      try {
        searchLoading.value = true;
        const response = await server.getErrorLog({
          filters: {
            status: status.value as ErrorStatus | '',
            user: user.value,
            description: description.value,
            stacktrace: stacktrace.value,
          },
          sort: {
            type: sort.value as SortType,
            desc: desc.value === 'true',
          },
          pagination: {
            page: Number(page.value),
            limit: Number(limit.value),
          },
        });
        errorList.value = response.errors;
        serverCount.value = response.count;
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading error log. Please try again',
          err as Error,
          'server.getErrorLog failed'
        );
      } finally {
        searchLoading.value = false;
      }
    };

    const formatTimestamp = (timestamp: Date) => {
      return DateTime.fromJSDate(new Date(timestamp)).toLocaleString(
        DateTime.DATETIME_MED
      );
    };

    const setupDialog = (error: ErrorsRowWithUser) => {
      dialogError.value = error;
      showErrorDetails.value = true;
    };

    const updateStatus = async (status: ErrorStatus) => {
      dialogError.value.status = status;
      await getErrorLog();
    };

    const updateMultipleStatus = async (status: ErrorStatus) => {
      try {
        const selectedErrorUuids = selectedErrors.value.map(
          error => error.uuid
        );
        await server.updateErrorStatus(selectedErrorUuids, status);
        await getErrorLog();
        selectedErrors.value = [];
        await resetAdminBadge();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error updating error status. Please try again.',
          err as Error
        );
      }
    };

    watch(
      searchOptions,
      async () => {
        try {
          page.value = String(searchOptions.value.page);
          limit.value = String(searchOptions.value.itemsPerPage);
          sort.value = searchOptions.value.sortBy[0];
          desc.value = String(searchOptions.value.sortDesc[0]);
          await getErrorLog();
        } catch (err) {
          actions.showErrorSnackbar(
            `Error updating error log. Please try again.`,
            err as Error
          );
        }
      },
      { deep: true }
    );

    watch([page, limit, sort, desc], () => {
      searchOptions.value.page = Number(page.value);
      searchOptions.value.itemsPerPage = Number(limit.value);
      searchOptions.value.sortBy = [sort.value];
      searchOptions.value.sortDesc = [Boolean(desc.value)];
    });

    watch(
      [status, user, description, stacktrace],
      _.debounce(async () => {
        try {
          searchOptions.value.page = 1;
          await getErrorLog();
        } catch (err) {
          actions.showErrorSnackbar(
            'Error filtering or sorting error log. Please try again.',
            err as Error
          );
        }
      }, 200),
      {
        immediate: false,
      }
    );

    return {
      loading,
      searchLoading,
      listHeaders,
      errorList,
      page,
      limit,
      sort,
      desc,
      status,
      user,
      description,
      stacktrace,
      searchOptions,
      server,
      showErrorDetails,
      getErrorLog,
      serverCount,
      formatTimestamp,
      dialogError,
      setupDialog,
      updateStatus,
      statusOptions,
      selectedErrors,
      updateMultipleStatus,
    };
  },
});
